// Shell-level reminder engine. Mounted once by Preview.jsx so the popup can
// fire regardless of which CRM module is active. Polls /follow-ups/active
// every 60s, then a local 5s ticker decides which reminder to show next.
//
// Design choices, copied from source CRM:
//  - Each reminder fires once per `intervalMinutes` until snoozed or done
//    (so an idle user gets nudged again, not just once)
//  - We chime continuously (1.2s loop) while the popup is open and the user
//    hasn't muted it. Audio Context is recreated each chime so a paused
//    tab waking up doesn't desync the loop.
//  - `actedRef` tracks leads the user just snoozed/completed so the next
//    /active refetch doesn't immediately re-pop the same lead before the
//    server reflects the new state.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useActiveFollowUps,
  useCompleteFollowUp,
  useSnoozeFollowUp,
} from "../hooks/useFollowUps";
import ReminderPopup from "../molecules/ReminderPopup";

const TICK_MS = 5000;

function playChime() {
  if (typeof window === "undefined") return;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const tones = [
      { f: 880, t: now, d: 0.18 },
      { f: 1318, t: now + 0.16, d: 0.22 },
    ];
    for (const { f, t, d } of tones) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = f;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.22, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + d);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + d + 0.05);
    }
    setTimeout(() => ctx.close().catch(() => {}), 700);
  } catch {
    // Audio not available — silent fail.
  }
}

export default function ReminderOverlay() {
  const { data, refetch } = useActiveFollowUps();
  const completeFollowUp = useCompleteFollowUp();
  const snoozeFollowUp = useSnoozeFollowUp();

  const reminders = useMemo(() => data?.reminders || [], [data?.reminders]);
  const settings = useMemo(
    () =>
      data?.settings || {
        enabled: true,
        beforeGapMinutes: 120,
        afterGapMinutes: 120,
        intervalMinutes: 15,
        audibleChime: true,
      },
    [data?.settings]
  );

  const remindersById = useMemo(() => {
    const map = new Map();
    for (const r of reminders) map.set(r.leadId, r);
    return map;
  }, [reminders]);

  const lastFiredRef = useRef(new Map());
  const queueRef = useRef([]);
  const actedRef = useRef(new Set());
  const [currentLeadId, setCurrentLeadId] = useState(null);
  const [muted, setMuted] = useState(false);

  const showNext = useCallback(() => {
    while (queueRef.current.length > 0) {
      const leadId = queueRef.current.shift();
      if (remindersById.has(leadId)) {
        setCurrentLeadId(leadId);
        return;
      }
    }
  }, [remindersById]);

  // Persistent chime while a reminder is active. Suppressed if the user
  // local-muted, or the org admin disabled audibleChime in settings.
  const audibleChime = settings.audibleChime !== false;
  useEffect(() => {
    if (!currentLeadId || muted || !audibleChime) return;
    playChime();
    const id = setInterval(playChime, 1200);
    return () => clearInterval(id);
  }, [currentLeadId, muted, audibleChime]);

  // Decide which lead to show next based on the window/interval rules.
  useEffect(() => {
    if (!settings.enabled) return;
    if (reminders.length === 0) return;

    const tick = () => {
      const now = Date.now();
      const intervalMs = settings.intervalMinutes * 60 * 1000;
      const beforeMs = settings.beforeGapMinutes * 60 * 1000;
      const afterMs = settings.afterGapMinutes * 60 * 1000;

      for (const r of reminders) {
        if (actedRef.current.has(r.leadId)) continue;

        const scheduled = new Date(r.scheduledAt).getTime();
        const windowStart = scheduled - beforeMs;
        const windowEnd = scheduled + afterMs;
        if (now < windowStart || now > windowEnd) continue;

        const snoozedUntil = r.snoozedUntil ? new Date(r.snoozedUntil).getTime() : 0;
        if (snoozedUntil > now) continue;

        const lastFired = lastFiredRef.current.get(r.leadId) ?? windowStart - intervalMs;
        if (now < lastFired + intervalMs) continue;

        lastFiredRef.current.set(r.leadId, now);
        if (currentLeadId !== r.leadId && !queueRef.current.includes(r.leadId)) {
          queueRef.current.push(r.leadId);
        }
      }

      if (!currentLeadId) showNext();
    };

    tick();
    const id = setInterval(tick, TICK_MS);
    return () => clearInterval(id);
  }, [
    reminders,
    settings.enabled,
    settings.intervalMinutes,
    settings.beforeGapMinutes,
    settings.afterGapMinutes,
    currentLeadId,
    showNext,
  ]);

  const markDone = useCallback(async () => {
    if (!currentLeadId) return;
    const leadId = currentLeadId;
    actedRef.current.add(leadId);
    queueRef.current = queueRef.current.filter((id) => id !== leadId);
    lastFiredRef.current.delete(leadId);
    setCurrentLeadId(null);
    try {
      await completeFollowUp.mutateAsync({ leadId });
    } catch {
      // popup already dismissed
    }
    await refetch();
    actedRef.current.delete(leadId);
    showNext();
  }, [currentLeadId, completeFollowUp, refetch, showNext]);

  const snooze = useCallback(async () => {
    if (!currentLeadId) return;
    const leadId = currentLeadId;
    actedRef.current.add(leadId);
    queueRef.current = queueRef.current.filter((id) => id !== leadId);
    setCurrentLeadId(null);
    try {
      await snoozeFollowUp.mutateAsync({ leadId });
    } catch {
      // swallow
    }
    await refetch();
    actedRef.current.delete(leadId);
    showNext();
  }, [currentLeadId, snoozeFollowUp, refetch, showNext]);

  const currentReminder = currentLeadId ? remindersById.get(currentLeadId) || null : null;
  const busy = completeFollowUp.isPending || snoozeFollowUp.isPending;

  // `setMuted` is plumbed but no UI exposes it yet — wire to a header
  // toggle once Rajiv wants a "do not disturb" button. Until then audio
  // can be silenced at the OS/tab level.
  void setMuted;

  if (!currentReminder) return null;

  return (
    <ReminderPopup
      reminder={currentReminder}
      settings={settings}
      onSnooze={snooze}
      onMarkDone={markDone}
      busy={busy}
    />
  );
}
